import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import { Bot, Inbox, Clock, ShieldCheck, Languages, Users } from 'lucide-react';
import { MarketingShell, PageHero, PageSection, CardGrid, FaqList, ClosingCta, buildFaqJsonLd, buildBreadcrumb } from '@/components/marketing/MarketingShell';

const FAQ = [
  {
    q: 'Apa itu AI customer service Nerava?',
    a: 'AI customer service Nerava adalah asisten yang membaca chat pelanggan dari WhatsApp, Instagram, Messenger, dan website, lalu menjawab memakai data toko Anda: katalog produk, stok, harga, ongkir, dan SOP. Jawaban yang tidak ada dasarnya tidak dikarang, melainkan dialihkan ke tim Anda.',
  },
  {
    q: 'Apakah AI bisa salah menjawab harga atau stok?',
    a: 'AI hanya boleh menjawab dari data yang Anda masukkan ke katalog. Kalau produk tidak ditemukan atau stok kosong, AI mengatakan apa adanya dan menawarkan alternatif, bukan mengarang angka.',
  },
  {
    q: 'Kapan percakapan dialihkan ke manusia?',
    a: 'Saat pelanggan komplain, meminta hal di luar SOP, atau AI tidak yakin. Percakapan langsung ditandai di inbox agar tim Anda bisa mengambil alih tanpa kehilangan riwayat chat.',
  },
  {
    q: 'Berapa lama setup-nya?',
    a: 'Rata-rata sekitar 15 menit: hubungkan channel, unggah katalog produk, isi FAQ dan SOP, lalu uji di simulator sebelum AI dipakai melayani pelanggan sungguhan.',
  },
];

const JSON_LD = [buildBreadcrumb('AI Customer Service', '/ai-customer-service'), buildFaqJsonLd(FAQ)];

const FEATURES = [
  { icon: <Bot className="w-5 h-5" />, title: 'Jawab sesuai data toko', description: 'AI membaca katalog produk, stok, varian, harga, dan ongkir Anda. Tidak ada jawaban karangan yang bikin pelanggan kecewa.' },
  { icon: <Inbox className="w-5 h-5" />, title: 'Satu inbox untuk semua channel', description: 'WhatsApp, Instagram, Facebook Messenger, dan widget website masuk ke satu tempat lengkap dengan riwayat pelanggan.' },
  { icon: <Clock className="w-5 h-5" />, title: 'Aktif 24 jam', description: 'Chat yang masuk tengah malam atau saat Anda sibuk tetap dijawab, jadi calon pembeli tidak kabur ke toko sebelah.' },
  { icon: <Users className="w-5 h-5" />, title: 'Serah terima ke tim', description: 'Komplain dan permintaan khusus otomatis ditandai agar tim CS mengambil alih dengan konteks lengkap.' },
  { icon: <Languages className="w-5 h-5" />, title: 'Gaya bahasa Anda', description: 'Atur nada bicara, sapaan, dan istilah khas toko Anda supaya pelanggan merasa bicara dengan orang yang sama.' },
  { icon: <ShieldCheck className="w-5 h-5" />, title: 'Channel resmi Meta', description: 'WhatsApp, Instagram, dan Messenger terhubung lewat API resmi Meta, bukan modifikasi aplikasi yang berisiko diblokir.' },
];

const AiCustomerServicePage = () => (
  <MarketingShell active="/ai-customer-service">
    <SEOHead
      title="AI Customer Service 24/7 untuk Toko Online | Nerava"
      description="AI customer service Nerava menjawab chat pelanggan dari katalog produk Anda sendiri: stok, harga, ongkir, dan SOP. Satu inbox untuk WhatsApp, Instagram & Messenger. Coba gratis 30 hari."
      canonical="https://nerava.id/ai-customer-service"
      jsonLd={JSON_LD}
    />
    <PageHero
      eyebrow="AI Customer Service"
      title="Pelanggan dibalas cepat,"
      highlight="tanpa jawaban karangan"
      subtitle="Nerava menjawab pertanyaan produk, stok, dan ongkir memakai data toko Anda sendiri. Tim Anda cukup turun tangan untuk hal yang memang butuh manusia."
      bullets={['Balasan di bawah 1 menit', 'Semua channel dalam satu inbox', 'Serah terima ke tim kapan saja']}
    />

    <PageSection title="Kenapa pakai AI, bukan balas manual" intro="Chat yang lambat dibalas adalah pesanan yang hilang. Nerava menutup jeda itu tanpa menambah jam kerja tim Anda." tinted>
      <CardGrid items={FEATURES} />
    </PageSection>

    <PageSection title="Cara kerjanya" intro="Empat langkah, semuanya dikerjakan sendiri dari dashboard tanpa coding.">
      <ol className="space-y-4 max-w-3xl">
        {[
          ['Hubungkan channel', 'Sambungkan WhatsApp, Instagram, Messenger, atau widget website ke Nerava.'],
          ['Isi data toko', 'Unggah katalog produk beserta foto, varian, stok, dan harga, lalu tambahkan FAQ dan SOP.'],
          ['Uji di simulator', 'Coba skenario nyata seperti tanya harga, minta foto, dan nego ongkir sebelum AI dipakai melayani pelanggan.'],
          ['Pantau dan rapikan', 'Lihat chat mana yang dialihkan ke tim, lalu perbaiki jawaban AI dari dashboard.'],
        ].map(([title, desc], i) => (
          <li key={title} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900/60">
            <span className="grid place-items-center w-9 h-9 flex-shrink-0 rounded-full bg-indigo-600 text-white text-sm font-semibold">{i + 1}</span>
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1">{title}</h3>
              <p className="text-sm text-slate-600 dark:text-zinc-400">{desc}</p>
            </div>
          </li>
        ))}
      </ol>
      <p className="mt-8 text-sm text-slate-500 dark:text-zinc-400">
        Khusus pembeli lewat WhatsApp, lihat{' '}
        <Link to="/ai-whatsapp" className="text-indigo-600 dark:text-indigo-400 hover:underline" data-testid="link-to-whatsapp-page">
          halaman AI WhatsApp
        </Link>
        . Untuk pengelolaan pesanan dan pelanggan, lihat{' '}
        <Link to="/crm-pesanan" className="text-indigo-600 dark:text-indigo-400 hover:underline" data-testid="link-to-crm-page">
          CRM & Pesanan
        </Link>
        .
      </p>
    </PageSection>

    <PageSection title="Pertanyaan yang sering masuk" tinted>
      <FaqList items={FAQ} />
    </PageSection>

    <ClosingCta
      title="Biarkan AI yang jaga chat malam ini"
      subtitle="Aktifkan trial 30 hari, hubungkan satu channel, dan lihat berapa pertanyaan pelanggan yang selesai tanpa Anda balas sendiri."
    />
  </MarketingShell>
);

export default AiCustomerServicePage;
